import { Divider, InfoPopover, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import {
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
  R1FactType,
  ThreatType,
} from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { NCCard, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { HabitatIntactnessExplainer } from '@/components/NCExplainers/HabitatIntactnessExplainer';
import { COMPLIANCE_LOGOS_HABITAT_INTACTNESS } from '@/config/constants';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';

import { HabitatIntactnessThreatsTableCard } from './TableCard';

export const HabitatIntactnessThreatsTable = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.habitatIntactness.threatsTable.labels.threatsTable')}>
      <HabitatIntactnessThreatsTablePlotCard />
    </PlotCardWrapper>
  );
};

const HabitatIntactnessThreatsTablePlotCard = () => {
  const { t } = useTranslation();

  const factNames = {
    timeSeries: R1FactType.threat_areas_historic_graph,
    threatArea: R1FactType.threat_areas_pct,
    area5yPct: R1FactType.threat_areas_pct_previous_5y_pct_change,
  };

  const facts = usePlotFacts(Object.values(factNames));

  const timeSeriesFact = getFactFromFactArray<GraphFact[]>(facts, factNames.timeSeries)?.value;

  const threatAreaFacts = {
    noThreat: getFactFromFactArray<number>(facts, factNames.threatArea, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.no_threat_area,
    }),
    cropland: getFactFromFactArray<number>(facts, factNames.threatArea, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.cropland,
    }),
    deforestation: getFactFromFactArray<number>(facts, factNames.threatArea, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.deforestation,
    }),
    built: getFactFromFactArray<number>(facts, factNames.threatArea, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.builtup,
    }),
    roads: getFactFromFactArray<number>(facts, factNames.threatArea, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.roads,
    }),
  };

  const area5yPct = {
    noThreat: getFactFromFactArray<number>(facts, factNames.area5yPct, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.no_threat_area,
    }),
    cropland: getFactFromFactArray<number>(facts, factNames.area5yPct, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.cropland,
    }),
    deforestation: getFactFromFactArray<number>(facts, factNames.area5yPct, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.deforestation,
    }),
    built: getFactFromFactArray<number>(facts, factNames.area5yPct, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.builtup,
    }),
    roads: getFactFromFactArray<number>(facts, factNames.area5yPct, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.roads,
    }),
  };

  const areaPctFact = Object.values(threatAreaFacts).filter(Boolean) as PlotReportFactElement<number>[];

  const fiveYearsPctChangeFact = Object.values(area5yPct).filter(Boolean) as PlotReportFactElement<number>[];

  const hasData = areaPctFact.length > 0 || fiveYearsPctChangeFact.length > 0 || timeSeriesFact?.length;

  if (!hasData) {
    return null;
  }

  return (
    <NCCard explainer={<HabitatIntactnessExplainer />} data-testid='habitat-intactness-threats-table-nca'>
      <NCCardHeader
        title={
          <Stack direction='row' spacing={2} className='w-full justify-end'>
            <span>{t('shared.ncaDetail.details.habitatIntactness.threatsTable.labels.threatsTable')}</span>
            <InfoPopover
              position='top'
              body={t('shared.ncaDetail.details.habitatIntactness.labels.missingDataContext')}
            />
          </Stack>
        }
        className='lg:grid-cols-[1fr_1fr]'
      />
      <HabitatIntactnessThreatsTableCard
        timeSeriesFact={timeSeriesFact}
        areaPctFact={areaPctFact}
        fiveYearsPctChangeFact={fiveYearsPctChangeFact}
      />
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_HABITAT_INTACTNESS} />
    </NCCard>
  );
};
